// src/app/features/auth/reducers/client.reducer.ts
import { createReducer, on } from '@ngrx/store';
import {
  IClient,
  IClientListFilter,
  IClientListSort,
  IClientName,
} from '../../models/client.model';
import { ClientActions } from './client.actions';
import { AuthActions } from '../auth/auth.actions';

export interface ClientState {
  client: IClient | null; // Allow both IClient and null
  clientNames: IClientName[];
  saveSuccess: boolean;
  saveFailure: boolean;
  clients: IClient[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  filter: IClientListFilter;
  sort: IClientListSort;
  error: string | null;
  allStatusClientNames?: IClientName[];
}

export const initialClientState: ClientState = {
  client: null, // Add this
  clients: [],
  clientNames: [],
  total: 0,
  page: 1,
  limit: 100,
  loading: false,
  filter: {},
  sort: {
    key: 'createdAt',
    order: 'desc',
  },
  error: null,
  saveSuccess: false,
  saveFailure: false,
};

export const clientReducer = createReducer(
  initialClientState,
  // Load Clients
  on(ClientActions.listClients, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ClientActions.listClientsSuccess, (state, { response, filters }) => ({
    ...state,
    clients: response.data.clients.list,
    total: response.data.clients.total,
    page: response.data.clients.page,
    limit: response.data.clients.limit,
    filter: filters.body?.filter ?? state.filter,
    loading: false,
    error: null,
  })),
  on(ClientActions.listClientsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(ClientActions.listClientNames, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ClientActions.listClientNamesSuccess, (state, { response }) => ({
    ...state,
    clientNames: response.data.clients,
    loading: false,
    error: null,
  })),
  on(ClientActions.listClientNamesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // For single client
  on(ClientActions.loadSingleClient, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ClientActions.loadSingleClientSuccess, (state, { client }) => ({
    ...state,
    client: client,
    loading: false,
    error: null,
  })),
  on(ClientActions.loadSingleClientFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Create Client
  on(ClientActions.createClient, (state) => ({
    ...state,
    loading: true,
    error: null,
    saveSuccess: false,
    saveFailure: false,
  })),
  on(ClientActions.createClientSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
    saveSuccess: true,
  })),
  on(ClientActions.createClientFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    saveFailure: true,
  })),

  // Update Client
  on(ClientActions.updateClient, (state) => ({
    ...state,
    loading: true,
    error: null,
    saveSuccess: false,
    saveFailure: false,
  })),
  on(ClientActions.updateClientSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
    saveSuccess: true,
  })),
  on(ClientActions.updateClientFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    saveFailure: true,
  })),

  // Delete Client
  on(ClientActions.deleteClient, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ClientActions.deleteClientSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(ClientActions.deleteClientFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(ClientActions.setClientQueryParams, (state, { params, body }) => ({
    ...state,
    page: params.page ?? state.page,
    limit: params.limit ?? state.limit,
    filter: body?.filter ?? state.filter,
    sort: body?.sort ?? state.sort,
  })),

  // Change  Client Status
  on(ClientActions.changeClientStatus, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ClientActions.changeClientStatusSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(ClientActions.changeClientStatusFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(ClientActions.clearSingleClient, (state) => ({
    ...state,
    client: null,
  })),
   // Load AllStatus Location Names
    on(ClientActions.loadAllStatusClientNames, (state) => ({
      ...state,
      loading: true,
      error: null,
    })),
    on(
      ClientActions.loadAllStatusClientNamesSuccess,
      (state, { response }) => ({
        ...state,
        loading: false,
        allStatusClientNames: response.data.clients || [],
      })
    ),
    on(ClientActions.loadAllStatusClientNamesFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
  on(AuthActions.logout, () => initialClientState),
);
